export type ModulePermission = {
  view: boolean;
  create: boolean;
  edit: boolean;
  delete: boolean;
};

export type RolePermissions = Record<string, ModulePermission>;

export type AuthUser = {
  email?: string;
  firstName?: string;
  token?: string;
  accessToken?: string;
  isVirtualUser?: boolean;
  parentAccount?: string | null;
  parentAccountName?: string | null;
  parentId?: number | null;
  role?: {
    _id: string;
    name: string;
    slug: string;
  } | null;
  permissions?: RolePermissions;
};

export const MODULE_MENU_ITEMS = [
  { moduleKey: "accounts", path: "/users", title: "Dealers & End Users", listPath: "/users/list" },
  { moduleKey: "devices", path: "/devices", title: "Devices", listPath: "/devices/list" },
  { moduleKey: "monitor", path: "/monitors", title: "Monitor", listPath: "/monitors" },
  {
    moduleKey: "tracker_pairing",
    path: "/tracker-pairing",
    title: "Pair IMEI - Camera",
    listPath: "/tracker-pairing",
  },
  {
    moduleKey: "quick_pair_imeis",
    path: "/quickPairImeis",
    title: "Pair IMEI - Manual Entry",
    listPath: "/quickPairImeis/list",
  },
  {
    moduleKey: "quick_transfers",
    path: "/quickTransfers",
    title: "Quick Customer Transfer",
    listPath: "/quickTransfers/list",
  },
  { moduleKey: "sold_reports", path: "/sold-reports", title: "Sold Reports", listPath: "/sold-reports" },
  {
    moduleKey: "keyper_assets",
    path: "/keyper/assets",
    title: "KEYper Assets",
    listPath: "/keyper/assets",
  },
] as const;

export const ROUTE_MODULE_MAP: Array<{ prefix: string; moduleKey: string }> = [
  { prefix: "/users", moduleKey: "accounts" },
  { prefix: "/devices", moduleKey: "devices" },
  { prefix: "/monitors", moduleKey: "monitor" },
  { prefix: "/tracker-pairing", moduleKey: "tracker_pairing" },
  { prefix: "/quickPairImeis", moduleKey: "quick_pair_imeis" },
  { prefix: "/quickTransfers", moduleKey: "quick_transfers" },
  { prefix: "/sold-reports", moduleKey: "sold_reports" },
  { prefix: "/keyper/assets", moduleKey: "keyper_assets" },
];

export const SHARED_ACTION_MODULE_MAP: Record<string, string> = {
  User: "accounts",
  Video: "quick_pair_imeis",
  QuickTransfer: "quick_transfers",
  KeyperAssets: "keyper_assets",
};

export const getModuleKeyForSharedAction = (sharedActionId: string) =>
  SHARED_ACTION_MODULE_MAP[sharedActionId] || null;

export const getAuthUser = (): AuthUser | null => {
  const raw = localStorage.getItem("kt-auth-react-v");
  if (!raw) {
    return null;
  }

  try {
    return JSON.parse(raw) as AuthUser;
  } catch {
    return null;
  }
};

export const isPortalAdmin = (user: AuthUser | null) =>
  user?.email?.toLowerCase() === process.env.REACT_APP_ADMIN?.toLowerCase();

export const isVirtualUser = (user: AuthUser | null = getAuthUser()) =>
  Boolean(user?.isVirtualUser);

export const hasModulePermission = (
  moduleKey: string,
  permission: keyof ModulePermission = "view",
  user: AuthUser | null = getAuthUser()
) => {
  if (!user?.isVirtualUser) {
    return true;
  }

  return Boolean(user.permissions?.[moduleKey]?.[permission]);
};

export const getModulePermissions = (
  moduleKey: string,
  user: AuthUser | null = getAuthUser()
) => ({
  canView: hasModulePermission(moduleKey, "view", user),
  canCreate: hasModulePermission(moduleKey, "create", user),
  canEdit: hasModulePermission(moduleKey, "edit", user),
  canDelete: hasModulePermission(moduleKey, "delete", user),
});

export const canPerformModuleAction = (
  moduleKey: string | null,
  permission: keyof ModulePermission,
  user: AuthUser | null = getAuthUser()
) => {
  if (!moduleKey) {
    return true;
  }

  return hasModulePermission(moduleKey, permission, user);
};

export const canShowMenuItem = (
  moduleKey: string,
  options?: { adminOnly?: boolean },
  user: AuthUser | null = getAuthUser()
) => {
  if (options?.adminOnly && !isPortalAdmin(user)) {
    return false;
  }

  if (!user?.isVirtualUser) {
    return true;
  }

  return hasModulePermission(moduleKey, "view", user);
};

export const getDefaultRoute = (user: AuthUser | null = getAuthUser()) => {
  if (!user?.isVirtualUser) {
    return "/users/list";
  }

  for (const item of MODULE_MENU_ITEMS) {
    if ("adminOnly" in item && item.adminOnly && !isPortalAdmin(user)) {
      continue;
    }

    if (hasModulePermission(item.moduleKey, "view", user)) {
      return item.listPath;
    }
  }

  // Stay authenticated — do not send virtual users to login just because
  // role permissions currently grant no modules.
  return "/access-denied";
};

export const getModuleKeyForPath = (pathname: string) => {
  const match = ROUTE_MODULE_MAP.find(({ prefix }) =>
    pathname.startsWith(prefix)
  );
  return match?.moduleKey || null;
};

export const canAccessPath = (
  pathname: string,
  user: AuthUser | null = getAuthUser()
) => {
  if (!user?.isVirtualUser) {
    return true;
  }

  const moduleKey = getModuleKeyForPath(pathname);
  if (!moduleKey) {
    return true;
  }

  return hasModulePermission(moduleKey, "view", user);
};
